import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, LayoutDashboard, Ticket, LogOut, UserCircle } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/authContext";
import { useRole } from "@/lib/roleContext";
import { Wordmark } from "@/components/Wordmark";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
    { to: "/", label: "Discover" },
    { to: "/events", label: "Events" },
    { to: "/artists", label: "Artists" },
    { to: "/venues", label: "Venues" },
];

export default function PublicNav() {
    const [open, setOpen] = useState(false);
    const { user, logout } = useAuth();
    const { role } = useRole();
    const navigate = useNavigate();
    const isAuthed = !!user && typeof user === "object";
    const showWorkspaceLink = isAuthed && ["artist_owner", "artist_team", "event_staff", "admin"].includes(role);
    // Fan Passport visibility is driven by the persistent server-side
    // account flag `has_fan_passport`. Same result across every browser
    // and device once the user has activated Fan Passport (or signed up
    // through the Fan intent).
    const showPassportBtn = isAuthed && !!user?.has_fan_passport;

    const doLogout = async () => {
        await logout();
        navigate("/", { replace: true });
    };

    return (
        <header className="sticky top-0 z-40 bg-[#0a0a0a] border-b border-[#2a2a2a]">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-6">
                <Link to="/" className="flex items-center gap-3" data-testid="brand-home-link">
                    <Wordmark />
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `px-4 py-2 text-sm font-display font-bold uppercase tracking-wider transition-colors ${
                                    isActive ? "text-[#ff4400]" : "text-[#f4f4f0] hover:text-[#ff4400]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden md:flex items-center gap-3">
                    {showPassportBtn && (
                        <button onClick={() => navigate("/fan")} className="pm-btn" data-testid="nav-passport-btn">
                            <Ticket className="w-4 h-4 mr-2" /> Passport
                        </button>
                    )}
                    {showWorkspaceLink && (
                        <button onClick={() => navigate("/workspace")} className="pm-btn" data-testid="nav-workspace-btn">
                            <LayoutDashboard className="w-4 h-4 mr-2" /> Workspace
                        </button>
                    )}
                    {isAuthed && user?.role === "admin" && (
                        <button onClick={() => navigate("/admin")} className="pm-btn" data-testid="nav-admin-btn">
                            Admin
                        </button>
                    )}
                    {isAuthed ? (
                        <DropdownMenu>
                            <DropdownMenuTrigger className="pm-btn pm-btn-primary" data-testid="nav-user-menu">
                                <UserCircle className="w-4 h-4 mr-2" />
                                <span className="text-xs truncate max-w-[140px]">{user?.name || user?.email}</span>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[#141414] border-[#2a2a2a] rounded-none text-[#f4f4f0] w-64">
                                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-[#666]">
                                    Signed in as
                                </DropdownMenuLabel>
                                <div className="px-2 pb-2 text-xs font-mono text-[#a3a3a3] truncate" data-testid="nav-user-email">
                                    {user.email}
                                </div>
                                <DropdownMenuSeparator className="bg-[#2a2a2a]" />
                                <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer rounded-none focus:bg-[#1f1f1f] focus:text-[#ff4400]" data-testid="nav-account-item">
                                    Account settings
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={doLogout} className="cursor-pointer rounded-none focus:bg-[#1f1f1f] focus:text-[#ff4400]" data-testid="nav-logout-item">
                                    <LogOut className="w-3 h-3 mr-2" /> Log out
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    ) : (
                        <>
                            <Link to="/signin" className="pm-btn" data-testid="nav-signin-btn">Sign in</Link>
                            <Link to="/signup" className="pm-btn pm-btn-primary" data-testid="nav-signup-btn">Create account</Link>
                        </>
                    )}
                </div>

                <button className="md:hidden p-2 border border-[#2a2a2a]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="menu">
                    {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>
            </div>

            {open && (
                <div className="md:hidden bg-[#0a0a0a] border-t border-[#2a2a2a] px-4 py-4 flex flex-col gap-2">
                    {links.map((l) => (
                        <NavLink key={l.to} to={l.to} end={l.to === "/"} onClick={() => setOpen(false)}
                            data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                            className={({ isActive }) => `px-3 py-3 font-display font-bold uppercase tracking-wider border-b border-[#2a2a2a] ${isActive ? "text-[#ff4400]" : "text-[#f4f4f0]"}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    {isAuthed ? (
                        <>
                            {showPassportBtn && (
                                <button className="pm-btn w-full mt-2" onClick={() => { setOpen(false); navigate("/fan"); }} data-testid="mobile-passport-btn">
                                    <Ticket className="w-4 h-4 mr-2" /> Passport
                                </button>
                            )}
                            {showWorkspaceLink && (
                                <button className="pm-btn w-full mt-2" onClick={() => { setOpen(false); navigate("/workspace"); }} data-testid="mobile-workspace-btn">
                                    Workspace
                                </button>
                            )}
                            <button className="pm-btn w-full mt-2" onClick={() => { setOpen(false); navigate("/account"); }} data-testid="mobile-account-btn">Account</button>
                            <button className="pm-btn w-full mt-2" onClick={() => { setOpen(false); doLogout(); }} data-testid="mobile-logout-btn">Log out</button>
                        </>
                    ) : (
                        <>
                            <Link to="/signin" onClick={() => setOpen(false)} className="pm-btn w-full mt-2 text-center" data-testid="mobile-signin-btn">Sign in</Link>
                            <Link to="/signup" onClick={() => setOpen(false)} className="pm-btn pm-btn-primary w-full mt-2 text-center" data-testid="mobile-signup-btn">Create account</Link>
                        </>
                    )}
                </div>
            )}
        </header>
    );
}
